import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Chip,
} from '@mui/material';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import EuroIcon from '@mui/icons-material/Euro';
import CheckIcon from '@mui/icons-material/Check';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPrices } from '../../services/storage';

const MotionBox = motion(Box);

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: 'easeOut' },
  }),
};

// Prices are always in EUR — only labels/notes are translated
const PRICE_DATA = [
  {
    id: 'einrichtung',
    number: '1',
    titleKey: 'preise.lohn_s1_title',
    subtitleKey: null,
    items: [
      { labelKey: 'preise.lohn_s1_i1', price: '79,90 €', noteKey: 'preise.lohn_note_net' },
      { labelKey: 'preise.lohn_s1_i2', price: '239,00 €', noteKey: 'preise.lohn_note_net' },
    ],
  },
  {
    id: 'abrechnung',
    number: '2',
    titleKey: 'preise.lohn_s2_title',
    subtitleKey: 'preise.lohn_s2_sub',
    items: [
      { labelKey: 'preise.lohn_s2_i1', price: '20,00 €', noteKey: 'preise.lohn_note_net' },
      { labelKey: 'preise.lohn_s2_i2', price: '19,00 €', noteKey: 'preise.lohn_note_net' },
      { labelKey: 'preise.lohn_s2_i3', price: '17,00 €', noteKey: 'preise.lohn_note_net' },
      { labelKey: 'preise.lohn_s2_i4', price: '27,00 €', noteKey: 'preise.lohn_note_net', highlight: true },
      { labelKey: 'preise.lohn_s2_i5', price: '25,00 €', noteKey: 'preise.lohn_note_net', highlight: true },
      { labelKey: 'preise.lohn_s2_i6', price: '23,00 €', noteKey: 'preise.lohn_note_net', highlight: true },
      { labelKey: 'preise.lohn_s2_i7', price: '20,00 €', noteKey: 'preise.lohn_note_net' },
      { labelKey: 'preise.lohn_s2_i8', price: '12,90 €', noteKey: 'preise.lohn_note_net' },
      { labelKey: 'preise.lohn_s2_i9', price: '19,90 €', noteKey: 'preise.lohn_note_net' },
      { labelKey: 'preise.lohn_s2_i10', price: '5,00 €', noteKey: 'preise.lohn_note_net' },
      { labelKey: 'preise.lohn_s2_i11', price: '15,00 €', noteKey: 'preise.lohn_note_net' },
      { labelKey: 'preise.lohn_s2_i12', price: '40,00 €', noteKey: 'preise.lohn_note_net' },
      { labelKey: 'preise.lohn_s2_i13', price: '9,50 €', noteKey: 'preise.lohn_note_net' },
    ],
  },
  {
    id: 'bescheinigungen',
    number: '3',
    titleKey: 'preise.lohn_s3_title',
    subtitleKey: null,
    items: [
      { labelKey: 'preise.lohn_s3_i1', price: '32,00 €', noteKey: 'preise.lohn_note_net' },
      { labelKey: 'preise.lohn_s3_i2', price: '32,00 €', noteKey: 'preise.lohn_note_net' },
      { labelKey: 'preise.lohn_s3_i3', price: '9,90 €', noteKey: 'preise.lohn_note_net' },
      { labelKey: 'preise.lohn_s3_i4', price: '49,90 €', noteKey: 'preise.lohn_note_net' },
      { labelKey: 'preise.lohn_s3_i5', price: '49,90 €', noteKey: 'preise.lohn_note_net' },
      { labelKey: 'preise.lohn_s3_i6', price: '65,00 €', noteKey: 'preise.lohn_note_net' },
    ],
  },
  {
    id: 'antraege',
    number: '4',
    titleKey: 'preise.lohn_s4_title',
    subtitleKey: null,
    items: [
      { labelKey: 'preise.lohn_s4_i1', price: '16,50 €', noteKey: null },
      { labelKey: 'preise.lohn_s4_i2', price: '118,00 €', noteKey: 'preise.lohn_s4_i2_note' },
    ],
  },
  {
    id: 'nebenleistungen',
    number: '5',
    titleKey: 'preise.lohn_s5_title',
    subtitleKey: null,
    items: [
      { labelKey: 'preise.lohn_s5_i1', price: '74,90 €', noteKey: 'preise.lohn_note_net' },
      { labelKey: 'preise.lohn_s5_i2', price: '74,90 €', noteKey: 'preise.lohn_note_net' },
      { labelKey: 'preise.lohn_s5_i3', price: '60,00 €', noteKey: 'preise.lohn_note_net' },
      { labelKey: 'preise.lohn_s5_i4', price: '9,90 €', noteKey: 'preise.lohn_note_net' },
      { labelKey: 'preise.lohn_s5_i5', price: '19,90 €', noteKey: 'preise.lohn_note_net' },
      { labelKey: 'preise.lohn_s5_i6', price: '39,90 €', noteKey: 'preise.lohn_note_net' },
    ],
  },
  {
    id: 'beratungen',
    number: '6',
    titleKey: 'preise.lohn_s6_title',
    subtitleKey: null,
    items: [
      { labelKey: 'preise.lohn_s6_i1', price: '110,00 €', noteKey: 'preise.lohn_note_net' },
      { labelKey: 'preise.lohn_s6_i2', price: '110,00 €', noteKey: 'preise.lohn_note_net' },
      { labelKey: 'preise.lohn_s6_i3', price: '60,00 €', noteKey: 'preise.lohn_note_net' },
    ],
  },
];

const PreisePage: React.FC = () => {
  const { t } = useLanguage();
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [pricesMap, setPricesMap] = useState<Record<string, string>>({});

  useEffect(() => {
    getPrices().then(setPricesMap).catch(() => {});
  }, []);

  // Merge API prices into PRICE_DATA (API overrides hardcoded values)
  const mergedData = PRICE_DATA.map((section) => ({
    ...section,
    items: section.items.map((item) => ({
      ...item,
      price: pricesMap[item.labelKey] ?? item.price,
    })),
  }));

  const sortedData = [...mergedData].sort((a, b) => a.items.length - b.items.length);

  return (
    <Box sx={{ bgcolor: '#f7f8fc', minHeight: '100vh' }}>
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #e8e8f0', pt: 16, pb: 8 }}>
        <Container maxWidth="lg">
          <MotionBox initial="hidden" animate="visible" custom={0} variants={fadeUp}>
            <Chip
              icon={<EuroIcon sx={{ fontSize: '14px !important' }} />}
              label={t('preise.lohn_chip')}
              sx={{
                bgcolor: '#eef0fd',
                color: '#3b52d4',
                fontWeight: 700,
                fontSize: '0.72rem',
                letterSpacing: '0.1em',
                mb: 3,
                border: '1px solid #d0d5f8',
              }}
            />
          </MotionBox>
          <MotionBox initial="hidden" animate="visible" custom={1} variants={fadeUp}>
            <Typography
              variant="h1"
              sx={{ fontSize: { xs: '2.2rem', md: '3.2rem' }, fontWeight: 800, mb: 2, color: '#0d1333' }}
            >
              {t('preise.lohn_title')}
            </Typography>
          </MotionBox>
          <MotionBox initial="hidden" animate="visible" custom={2} variants={fadeUp}>
            <Typography sx={{ color: '#666', fontSize: '1.05rem', maxWidth: 560, lineHeight: 1.8 }}>
              {t('preise.lohn_subtitle')}
            </Typography>
          </MotionBox>
        </Container>
      </Box>

      {/* ── Sticky Section Nav ──────────────────────────────────────────────── */}
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          bgcolor: '#fff',
          borderBottom: '1px solid #e8e8f0',
          boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          overflowX: 'auto',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', gap: 0.5, py: 1.5 }}>
            {sortedData.map((s, i) => (
              <Button
                key={s.id}
                size="small"
                onClick={() => {
                  document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  setActiveSection(s.id);
                }}
                sx={{
                  whiteSpace: 'nowrap',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  px: 2,
                  py: 0.75,
                  borderRadius: 2,
                  color: activeSection === s.id ? '#fff' : '#444',
                  bgcolor: activeSection === s.id ? '#1e3a5f' : 'transparent',
                  '&:hover': { bgcolor: activeSection === s.id ? '#1e3a5f' : '#f0f2fa' },
                }}
              >
                {i + 1}. {t(s.titleKey).split(' ').slice(0, 2).join(' ')}
              </Button>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── Sections ────────────────────────────────────────────────────────── */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 4, alignItems: 'start' }}>
          {sortedData.map((section, si) => (
            <MotionBox
              key={section.id}
              id={section.id}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              custom={si}
              variants={fadeUp}
            >
              <Box
                sx={{
                  bgcolor: '#fff',
                  borderRadius: 3,
                  overflow: 'hidden',
                  border: '1px solid #e8e8f0',
                  boxShadow: '0 2px 16px rgba(0,0,0,0.04)',
                }}
              >
                {/* Section Header */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    px: 4,
                    py: 3,
                    borderBottom: '1px solid #f0f0f8',
                    background: 'linear-gradient(135deg, #1e3a5f 0%, #2f527b 100%)',
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      bgcolor: 'rgba(255,255,255,0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#fff' }}>
                      {si + 1}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#fff' }}>
                      {t(section.titleKey)}
                    </Typography>
                    {section.subtitleKey && (
                      <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.65)', mt: 0.25 }}>
                        {t(section.subtitleKey)}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Items */}
                <Box>
                  {section.items.map((item, ii) => (
                    <Box
                      key={ii}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 3,
                        px: 4,
                        py: 2.5,
                        borderBottom: ii < section.items.length - 1 ? '1px solid #f5f5fa' : 'none',
                        bgcolor: ('highlight' in item && item.highlight) ? '#fafbff' : 'transparent',
                        '&:hover': { bgcolor: '#f7f8fd' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                        <CheckIcon sx={{ fontSize: 16, color: '#3b7dd8', mt: 0.3, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.9rem', color: '#333', lineHeight: 1.6 }}>
                          {t(item.labelKey)}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                        <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#1e3a5f', whiteSpace: 'nowrap' }}>
                          {item.price}
                        </Typography>
                        {item.noteKey && (
                          <Typography sx={{ fontSize: '0.72rem', color: '#999', mt: 0.2 }}>
                            {t(item.noteKey)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </MotionBox>
          ))}
        </Box>

        {/* ── Legal Note ──────────────────────────────────────────────────── */}
        <MotionBox
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
          variants={fadeUp}
          sx={{ mt: 5 }}
        >
          <Box
            sx={{
              bgcolor: '#fff',
              border: '1px solid #e8e8f0',
              borderLeft: '4px solid #1e3a5f',
              borderRadius: 2,
              px: 4,
              py: 3,
            }}
          >
            <Typography sx={{ fontSize: '0.88rem', color: '#555', lineHeight: 1.8 }}>
              <Box component="span" sx={{ fontWeight: 700, color: '#1e3a5f' }}>
                {t('preise.lohn_legal').split(':')[0]}:{' '}
              </Box>
              {t('preise.lohn_legal').split(':').slice(1).join(':')}
            </Typography>
          </Box>
        </MotionBox>

        {/* ── CTA ─────────────────────────────────────────────────────────── */}
        <MotionBox
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={1}
          variants={fadeUp}
          sx={{ mt: 6, textAlign: 'center' }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5, color: '#0d1333' }}>
            {t('preise.lohn_cta_title')}
          </Typography>
          <Typography sx={{ color: '#666', mb: 4, maxWidth: 440, mx: 'auto' }}>
            {t('preise.lohn_cta_text')}
          </Typography>
          <Button
            component={RouterLink}
            to="/contact"
            variant="contained"
            size="large"
            sx={{
              background: 'linear-gradient(135deg, #1e3a5f, #2f527b)',
              px: 5,
              py: 1.75,
              borderRadius: 2,
              fontWeight: 700,
              fontSize: '1rem',
              boxShadow: '0 8px 24px rgba(30,58,95,0.25)',
              '&:hover': {
                background: 'linear-gradient(135deg, #16304f, #264669)',
                boxShadow: '0 12px 32px rgba(30,58,95,0.35)',
              },
            }}
          >
            {t('preise.lohn_cta_btn')}
          </Button>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default PreisePage;
